import { clsx } from "clsx";
import { Loader2, Send, Trash2 } from "lucide-react";
import { Fragment, useMemo, useRef, useState } from "react";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
	useComments,
	useCreateComment,
	useDeleteComment,
} from "@/features/feed/hooks/use-comments.ts";
import type { Comment, Post } from "@/features/feed/types.ts";
import { avatarGradient, initialsOf } from "@/features/feed/utils/avatar.ts";
import { formatRelativeTime } from "@/features/feed/utils/format.ts";
import {
	insertMentionAt,
	type MentionDraft,
	type MentionQuery,
	mentionQueryAt,
	parseMentionSegments,
	toWireBody,
	wireMentionIds,
} from "@/features/feed/utils/mentions.ts";
import { canDeleteComment } from "@/features/feed/utils/social.ts";
import { useMembers } from "@/features/team/hooks/use-members.ts";
import { useMe } from "@/hooks/use-me.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";

const MAX_BODY_LENGTH = 2000;
const MAX_MENTIONS = 10;
const MAX_MENTION_OPTIONS = 6;

function normalize(s: string) {
	return s
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "");
}

function CommentBody({ comment }: { comment: Comment }) {
	const segments = parseMentionSegments(comment.body, comment.mentions);
	return (
		<p className="comments-section__body">
			{segments.map((segment) =>
				segment.kind === "mention" ? (
					<span key={segment.start} className="comments-section__mention">
						@{segment.text}
					</span>
				) : (
					<Fragment key={segment.start}>{segment.text}</Fragment>
				),
			)}
		</p>
	);
}

interface CommentItemProps {
	comment: Comment;
	canDelete: boolean;
	onDelete: () => void;
}

function CommentItem({ comment, canDelete, onDelete }: CommentItemProps) {
	const authorName = comment.author.removed
		? "Usuário removido"
		: (comment.author.name ?? "Usuário removido");

	return (
		<div className="comments-section__item">
			<Avatar size="sm">
				<AvatarFallback
					className="comments-section__avatar-fallback"
					style={{ background: avatarGradient(authorName) }}
				>
					{initialsOf(authorName)}
				</AvatarFallback>
			</Avatar>
			<div className="comments-section__content">
				<div className="comments-section__item-header">
					<span className="comments-section__author">{authorName}</span>
					{comment.author.role && !comment.author.removed && (
						<Badge variant="outline" className="comments-section__role-badge">
							{comment.author.role}
						</Badge>
					)}
					<span className="comments-section__time">
						{formatRelativeTime(comment.createdAt)}
					</span>
					{canDelete && (
						<Button
							variant="ghost"
							size="icon-sm"
							aria-label="Excluir comentário"
							className="comments-section__delete"
							onClick={onDelete}
						>
							<Trash2 />
						</Button>
					)}
				</div>
				<CommentBody comment={comment} />
			</div>
		</div>
	);
}

export function CommentsSection({ post }: { post: Post }) {
	const me = useMe();
	const { has, isAdmin } = usePermissions();
	const commentsQuery = useComments(post.id);
	const createMutation = useCreateComment(post.id);
	const deleteMutation = useDeleteComment(post.id);
	const membersQuery = useMembers();

	const [body, setBody] = useState("");
	const [drafts, setDrafts] = useState<MentionDraft[]>([]);
	const [mentionQuery, setMentionQuery] = useState<MentionQuery | null>(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [pendingDelete, setPendingDelete] = useState<Comment | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const viewer = {
		userId: me.data?.user.id,
		admin: isAdmin,
		role: me.data?.account?.role?.name,
	};

	const candidates = useMemo(() => {
		if (!mentionQuery) return [];
		const q = normalize(mentionQuery.query.trim());
		return (membersQuery.data ?? [])
			.filter((member) => !q || normalize(member.name).includes(q))
			.slice(0, MAX_MENTION_OPTIONS);
	}, [membersQuery.data, mentionQuery]);
	const menuOpen = mentionQuery !== null && candidates.length > 0;

	const wireBody = useMemo(
		() => toWireBody(body, drafts).trim(),
		[body, drafts],
	);
	const tooManyMentions = wireMentionIds(wireBody).length > MAX_MENTIONS;
	const canSubmit =
		wireBody.length > 0 &&
		wireBody.length <= MAX_BODY_LENGTH &&
		!tooManyMentions &&
		!createMutation.isPending;

	function syncMentionQuery(value: string, caret: number) {
		setMentionQuery(mentionQueryAt(value, caret));
		setActiveIndex(0);
	}

	function pick(member: { userId: string; name: string }) {
		if (!mentionQuery) return;
		const caret = textareaRef.current?.selectionStart ?? body.length;
		const next = insertMentionAt(body, mentionQuery.start, caret, member.name);
		setBody(next.text);
		setDrafts((prev) =>
			prev.some((draft) => draft.id === member.userId)
				? prev
				: [...prev, { id: member.userId, name: member.name }],
		);
		setMentionQuery(null);
		requestAnimationFrame(() => {
			const el = textareaRef.current;
			el?.focus();
			el?.setSelectionRange(next.caret, next.caret);
		});
	}

	function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
		if (!menuOpen) return;
		if (event.key === "ArrowDown") {
			event.preventDefault();
			setActiveIndex((index) => (index + 1) % candidates.length);
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setActiveIndex(
				(index) => (index - 1 + candidates.length) % candidates.length,
			);
		} else if (event.key === "Enter" || event.key === "Tab") {
			event.preventDefault();
			const candidate = candidates[activeIndex];
			if (candidate) pick(candidate);
		} else if (event.key === "Escape") {
			event.preventDefault();
			setMentionQuery(null);
		}
	}

	function submit() {
		setError(null);
		createMutation.mutate(
			{ body: wireBody },
			{
				onSuccess: () => {
					setBody("");
					setDrafts([]);
					setMentionQuery(null);
				},
				onError: () => {
					setError("Não foi possível enviar o comentário. Tente novamente.");
				},
			},
		);
	}

	function confirmDelete() {
		if (!pendingDelete) return;
		deleteMutation.mutate(pendingDelete.id, {
			onSettled: () => setPendingDelete(null),
		});
	}

	return (
		<div className="comments-section">
			{commentsQuery.isPending ? (
				<div className="comments-section__loading">
					<Loader2 />
				</div>
			) : commentsQuery.isError ? (
				<p className="comments-section__error">
					Não foi possível carregar os comentários.
				</p>
			) : commentsQuery.data.length === 0 ? (
				<p className="comments-section__empty">
					Nenhum comentário ainda. Seja o primeiro a interagir.
				</p>
			) : (
				<div className="comments-section__list">
					{commentsQuery.data.map((comment) => (
						<CommentItem
							key={comment.id}
							comment={comment}
							canDelete={canDeleteComment(comment, post, viewer)}
							onDelete={() => setPendingDelete(comment)}
						/>
					))}
				</div>
			)}

			{has("posts:comment") && (
				<div className="comments-section__composer">
					<div className="comments-section__composer-box">
						<Textarea
							ref={textareaRef}
							value={body}
							onChange={(event) => {
								setBody(event.target.value);
								syncMentionQuery(
									event.target.value,
									event.target.selectionStart,
								);
							}}
							onSelect={(event) => {
								const el = event.currentTarget;
								syncMentionQuery(el.value, el.selectionStart);
							}}
							onKeyDown={handleKeyDown}
							placeholder="Escreva um comentário... use @ para mencionar"
							maxLength={MAX_BODY_LENGTH}
							className="comments-section__textarea"
							aria-autocomplete="list"
							aria-expanded={menuOpen}
						/>
						{menuOpen && (
							<div
								className="comments-section__mention-menu"
								role="listbox"
								aria-label="Mencionar membro"
							>
								{candidates.map((member, index) => (
									<button
										key={member.userId}
										type="button"
										role="option"
										aria-selected={index === activeIndex}
										className={clsx(
											"comments-section__mention-option",
											index === activeIndex && "is-active",
										)}
										onMouseDown={(event) => event.preventDefault()}
										onClick={() => pick(member)}
									>
										{member.name}
									</button>
								))}
							</div>
						)}
					</div>
					<div className="comments-section__composer-footer">
						<span className="comments-section__counter">
							{wireBody.length}/{MAX_BODY_LENGTH}
						</span>
						<Button type="button" size="sm" disabled={!canSubmit} onClick={submit}>
							{createMutation.isPending ? (
								<Loader2 className="comments-section__spinner" />
							) : (
								<Send />
							)}
							Comentar
						</Button>
					</div>
					{tooManyMentions && (
						<p className="comments-section__error">
							Máximo de {MAX_MENTIONS} menções por comentário.
						</p>
					)}
					{error && <p className="comments-section__error">{error}</p>}
				</div>
			)}

			<ConfirmDialog
				open={pendingDelete !== null}
				onOpenChange={(open) => {
					if (!open) setPendingDelete(null);
				}}
				title="Excluir comentário"
				description="O comentário será removido para todos. Esta ação não pode ser desfeita."
				confirmLabel="Excluir"
				destructive
				isPending={deleteMutation.isPending}
				onConfirm={confirmDelete}
			/>
		</div>
	);
}
